// imports
import { createContext, FC, useCallback, useEffect, useState } from 'react';

// components
import { Alert } from 'components/common/Alert';

// interfaces, reducer
import { HTTP_STATUS } from 'constants/index';
import { useAuthContext } from 'contexts/AuthContext';
import { Subscription, SubscriptionPayload, useGetCurrentUserSubscriptionLazyQuery } from 'generated/graphql';
import { ChildrenType, UserSubscriptionsContextType } from 'interfaces';
import { isSuperAdmin } from 'lib/helper';

export const UserSubscriptionsContext = createContext<UserSubscriptionsContextType>({
  userSubscription: null,
  setUserSubscription: (subscription: SubscriptionPayload['subscription']) => {},
  hasSubscriptionFetched: false,
  refetchSubscription: async () => {},
});

export const UserSubscriptionsContextProvider: FC<ChildrenType> = ({ children }) => {
  const [userSubscription, setUserSubscription] = useState<SubscriptionPayload['subscription']>(null);
  const [hasSubscriptionFetched, setHasSubscriptionFetched] = useState(false);
  const { userRoles, isLoaded, isLoggedIn } = useAuthContext();
  const isSuper = isSuperAdmin(userRoles);

  const [findActiveSubScription] = useGetCurrentUserSubscriptionLazyQuery({
    onCompleted: (data) => {
      const { getCurrentUserSubscription } = data || {};
      const { subscription, response } = getCurrentUserSubscription || {};
      const { status, message } = response || {};

      if (status === HTTP_STATUS.SUCCESS) {
        setUserSubscription(subscription as Subscription);
        setHasSubscriptionFetched(true);
      } else {
        Alert.error(message ?? '');
      }
      setHasSubscriptionFetched(true);
    },
    onError: () => {
      setUserSubscription(null);
      setHasSubscriptionFetched(true);
    },
  });

  const fetchSubscriptions = useCallback(async () => {
    if (isLoaded) {
      if (isLoggedIn) {
        if (!isSuper) {
          await findActiveSubScription();
        } else {
          setHasSubscriptionFetched(true);
        }
      } else {
        setHasSubscriptionFetched(true);
      }
    }
  }, [isLoaded, isSuper, isLoggedIn, findActiveSubScription]);

  useEffect(() => {
    isLoaded && fetchSubscriptions();
  }, [fetchSubscriptions, isLoaded]);

  const refetchSubscription = useCallback(async () => {
    await findActiveSubScription();
  }, [findActiveSubScription]);

  return (
    <UserSubscriptionsContext.Provider
      value={{
        userSubscription,
        setUserSubscription,
        hasSubscriptionFetched,
        refetchSubscription,
      }}>
      {children}
    </UserSubscriptionsContext.Provider>
  );
};
